import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { ICON_BASE_PATH } from "../../../../utils/config";

interface FormUploadfileProps {
  onChange: (file: File | null) => void;
  value?: File | null;
  validationMessage?: string;
  touched?: boolean;
  accept?: string;
  label: string;
  maxSizeMB?: number;
}

type FileKind = "pdf" | "jpg" | "png" | "doc" | "xls" | "file";

const getFileKind = (file: File): FileKind => {
  const name = file.name.toLowerCase();
  const mime = file.type.toLowerCase();

  if (mime === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (mime.includes("jpeg") || name.endsWith(".jpg") || name.endsWith(".jpeg"))
    return "jpg";
  if (mime.includes("png") || name.endsWith(".png")) return "png";
  if (name.endsWith(".doc") || name.endsWith(".docx")) return "doc";
  if (name.endsWith(".xls") || name.endsWith(".xlsx")) return "xls";
  return "file";
};

const FILE_BADGE: Record<FileKind, { label: string; bg: string; fg: string }> =
  {
    pdf: { label: "PDF", bg: "#E4F4F4", fg: "#1B8A8A" },
    jpg: { label: "JPG", bg: "#FCEFE3", fg: "#D98324" },
    png: { label: "PNG", bg: "#FCEFE3", fg: "#D98324" },
    doc: { label: "DOC", bg: "#E5EEFB", fg: "#2A5FBE" },
    xls: { label: "XLS", bg: "#E6F4EA", fg: "#1E8A4C" },
    file: { label: "FILE", bg: "#EFEFEF", fg: "#666666" },
  };

const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(bytes % (1024 * 1024) === 0 ? 0 : 1)} MB`;
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${bytes} B`;
};

const Uploadfile: React.FC<FormUploadfileProps> = ({
  onChange,
  value,
  label,
  validationMessage,
  touched,
  accept,
  maxSizeMB = 5,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(value ?? null);
  const [sizeError, setSizeError] = useState<string>("");

  useEffect(() => {
    setFile(value ?? null);
  }, [value]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    e.target.value = "";
    if (!selected) return;

    if (selected.size > maxSizeMB * 1024 * 1024) {
      setSizeError(
        `${t("max_file_size_exceeded") || `File exceeds ${maxSizeMB} MB limit`}`,
      );
      return;
    }

    setSizeError("");
    setFile(selected);
    onChange(selected);
  };

  const handleRemove = () => {
    setFile(null);
    setSizeError("");
    onChange(null);
  };

  const showError = (touched && validationMessage) || sizeError;

  if (file) {
    const kind = getFileKind(file);
    const badge = FILE_BADGE[kind];

    return (
      <div className="">
        <label className="block mb-2 gx-font-semibold text-sm text-[#2E3338]">
          {t(label)}
        </label>

        <div className="w-full flex items-center justify-between gap-3 border border-[#D9D9D9] rounded-xl px-3 py-2.5">
          <div className="flex items-center gap-2.5 min-w-0">
            <span
              className="flex items-center justify-center shrink-0 rounded-md text-[10px] font-bold tracking-wide px-1.5 py-1 leading-none"
              style={{ backgroundColor: badge.bg, color: badge.fg }}
            >
              {badge.label}
            </span>
            <div className="min-w-0">
              <p className="text-sm gx-font-semibold text-[#2E3338] truncate mb-0!">
                {file.name}
              </p>
              <p className="text-xs text-[#999999] mb-0!">
                {formatFileSize(file.size)}
              </p>
            </div>
          </div>

          <button
            type="button"
            aria-label={t("remove_file") || "Remove file"}
            onClick={handleRemove}
            className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-[#2E3338] hover:bg-black transition-colors"
          >
            <img
              src={`${ICON_BASE_PATH}close-icon.svg`}
              width={10}
              height={10}
              alt=""
              className="invert"
            />
          </button>
        </div>

        {showError && (
          <p className="text-sm text-red-500 mt-2">
            {sizeError || validationMessage}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="">
      <label className="block mb-2 gx-font-semibold text-sm text-[#2E3338]">
        {t(label)}
      </label>

      <div
        className="w-full h-30 border-2 border-dashed border-[#D9D9D9] rounded-xl flex flex-col justify-center items-center text-center cursor-pointer hover:border-[#999]"
        onClick={() => inputRef.current?.click()}
      >
        <img
          src={`${ICON_BASE_PATH}fileIcon.svg`}
          alt=""
          className="w-6 h-6 mb-2"
        />
        <p className="text-sm gx-font-bold text-[#2E3338] mb-0!">
          {t("drag_and_drop")} {t("or")}{" "}
          <span className="text-[#1993D5]">{t("choose")}</span>
          {t("file")}
          {t("to")} {t("upload")}
        </p>
        <p className="text-xs text-[#999999] mt-1 mb-0!">
          {t("max")} {t("file")} {t("size")} {maxSizeMB} MB
        </p>

        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={accept || "image/*,.pdf,.doc,.docx"}
          onChange={handleFileChange}
        />
      </div>

      {showError && (
        <p className="text-sm text-red-500 mt-2">
          {sizeError || validationMessage}
        </p>
      )}
    </div>
  );
};

export default Uploadfile;
